/*global payhere*/
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuthContext } from "@asgardeo/auth-react";
import { Helmet } from "react-helmet-async";
import { Link, useSearchParams } from "react-router-dom";
import DialogModal from "../../components/Modal/DialogModal";
import MainLayout from "../../layouts/MainLayout";
import ChatStatusBanner from "../../components/ChatPricing/ChatStatusBanner";
import {
  CHAT_DB_SERVER_URL,
  NOTIFY_URL,
  PAYMENTS_SERVER_URL,
} from "../../config/backend-endpoints";

const PRODUCT_FEATURES = [
  {
    title: "Legal research, without query gymnastics",
    description:
      "Ask legal questions in plain English and move from issue spotting to usable analysis faster.",
  },
  {
    title: "Draft with case-law context",
    description:
      "Use the workspace to summarise material, draft content, and refine legal writing with stronger context.",
  },
  {
    title: "Built for Sri Lankan legal work",
    description:
      "A focused workspace tailored for legal professionals, not a generic public chatbot.",
  },
];

const INCLUDED_ITEMS = [
  "Monthly access to chat.paralegal.lk",
  "AI-assisted legal research workflows",
  "Drafting and summarisation workspace",
  "Ongoing platform improvements and feature updates",
];

const GROUP_PLAN_HIGHLIGHTS = [
  "Multi-user access for legal teams",
  "Coordinated onboarding support",
  "Subscription structure aligned to team usage",
];

const STATUS_OPTIONS = new Set([
  "active",
  "pending_activation",
  "activation_failed",
  "inactive",
  "manual_review",
  "trial_active",
  "trial_ending_soon",
  "trial_expired_access_paused",
  "trial_pending_activation",
  "trial_activation_failed",
  // Legacy URL/query compatibility states
  "success",
  "activating",
  "pending",
  "failed",
  "already_subscribed",
]);

const TRIAL_PLAN_CODE = "CHAT_PRO_MONTHLY";
const TRIAL_LIFECYCLE_STATUS_OPTIONS = new Set([
  "trialing",
  "converted_to_paid",
  "expired",
  "access_removal_started",
]);

const LEGACY_STATUS_TO_CANONICAL = {
  success: "active",
  already_subscribed: "active",
  activating: "pending_activation",
  pending: "pending_activation",
  failed: "activation_failed",
};

const normalizeUiStatus = (value) => {
  const normalized = String(value || "").trim().toLowerCase();
  if (!normalized) {
    return null;
  }
  if (LEGACY_STATUS_TO_CANONICAL[normalized]) {
    return LEGACY_STATUS_TO_CANONICAL[normalized];
  }
  if (STATUS_OPTIONS.has(normalized)) {
    return normalized;
  }
  return null;
};

const normalizeTrialLifecycleStatus = (value) => {
  const normalized = String(value || "").trim().toLowerCase();
  if (!normalized) {
    return null;
  }
  if (TRIAL_LIFECYCLE_STATUS_OPTIONS.has(normalized)) {
    return normalized;
  }
  return null;
};

const toOptionalIsoDate = (value) => {
  const normalized = String(value || "").trim();
  if (!normalized) {
    return null;
  }
  const parsed = new Date(normalized);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }
  return parsed.toISOString();
};

const calculateTrialDaysRemaining = (trialEndsAtIso) => {
  if (!trialEndsAtIso) {
    return null;
  }
  const trialEndsAtMs = Date.parse(trialEndsAtIso);
  if (Number.isNaN(trialEndsAtMs)) {
    return null;
  }
  const diffMs = trialEndsAtMs - Date.now();
  return Math.max(0, Math.ceil(diffMs / 86400000));
};

const formatTrialEndDate = (trialEndsAtIso) => {
  if (!trialEndsAtIso) {
    return "";
  }
  const parsed = new Date(trialEndsAtIso);
  if (Number.isNaN(parsed.getTime())) {
    return "";
  }
  return parsed.toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const normalizeTrialDaysRemaining = (value, trialEndsAtIso) => {
  if (value !== null && value !== undefined && value !== "") {
    const numeric = Number(value);
    if (Number.isFinite(numeric)) {
      return Math.max(0, Math.ceil(numeric));
    }
  }
  return calculateTrialDaysRemaining(trialEndsAtIso);
};

const extractTrialProjection = (payload) => {
  if (!payload || typeof payload !== "object") {
    return null;
  }

  let trialStatus = normalizeTrialLifecycleStatus(payload?.trial_status);
  if (!trialStatus) {
    trialStatus = normalizeTrialLifecycleStatus(payload?.status);
  }

  const reason = String(payload?.reason || "").trim().toLowerCase();
  if (!trialStatus && reason === "trial_started_pending_activation") {
    trialStatus = "trialing";
  }

  let trialEndsAt = toOptionalIsoDate(payload?.trial_ends_at);
  if (!trialEndsAt && reason.startsWith("trial_")) {
    trialEndsAt = toOptionalIsoDate(payload?.effective_to);
  }

  const accessActionStatus = String(payload?.access_action_status || "").trim().toLowerCase() || null;
  const trialDaysRemaining = normalizeTrialDaysRemaining(payload?.trial_days_remaining, trialEndsAt);
  const backendEligible =
    typeof payload?.trial_eligible === "boolean" ? payload.trial_eligible : null;
  const existing = typeof payload?.existing === "boolean" ? payload.existing : null;

  const hasTrialFields =
    trialStatus !== null ||
    trialEndsAt !== null ||
    trialDaysRemaining !== null ||
    accessActionStatus !== null ||
    backendEligible !== null ||
    existing !== null;

  if (!hasTrialFields) {
    return null;
  }

  return {
    trialStatus,
    trialEndsAt,
    trialDaysRemaining,
    accessActionStatus,
    backendEligible,
    existing,
  };
};

const mergeTrialProjection = (previous, incoming) => {
  if (!incoming) {
    return previous;
  }
  if (!previous) {
    return incoming;
  }

  return {
    trialStatus: incoming.trialStatus || previous.trialStatus || null,
    trialEndsAt: incoming.trialEndsAt || previous.trialEndsAt || null,
    trialDaysRemaining:
      incoming.trialDaysRemaining !== null && incoming.trialDaysRemaining !== undefined
        ? incoming.trialDaysRemaining
        : previous.trialDaysRemaining,
    accessActionStatus: incoming.accessActionStatus || previous.accessActionStatus || null,
    backendEligible:
      incoming.backendEligible !== null && incoming.backendEligible !== undefined
        ? incoming.backendEligible
        : previous.backendEligible,
    existing:
      incoming.existing !== null && incoming.existing !== undefined
        ? incoming.existing
        : previous.existing,
  };
};

const deriveTrialUiStatus = (canonicalStatus, trialProjection) => {
  const trialStatus = normalizeTrialLifecycleStatus(trialProjection?.trialStatus);
  if (!trialStatus || trialStatus === "converted_to_paid") {
    return null;
  }

  if (trialStatus === "expired" || trialStatus === "access_removal_started") {
    return "trial_expired_access_paused";
  }

  const accessActionStatus = String(trialProjection?.accessActionStatus || "")
    .trim()
    .toLowerCase();
  if (
    canonicalStatus === "activation_failed" ||
    accessActionStatus === "pending_activation_enqueue_failed"
  ) {
    return "trial_activation_failed";
  }
  if (
    canonicalStatus === "pending_activation" ||
    accessActionStatus.startsWith("pending_activation")
  ) {
    return "trial_pending_activation";
  }

  if ((trialProjection?.trialDaysRemaining ?? null) !== null && trialProjection.trialDaysRemaining <= 2) {
    return "trial_ending_soon";
  }

  return "trial_active";
};

const resolveUiProjection = (payload, priorTrialProjection = null) => {
  const canonicalStatus = mapBackendProjectionToUiStatus(payload);
  const mergedTrialProjection = mergeTrialProjection(
    priorTrialProjection,
    extractTrialProjection(payload)
  );
  const trialUiStatus = deriveTrialUiStatus(canonicalStatus, mergedTrialProjection);

  return {
    canonicalStatus,
    status: trialUiStatus || canonicalStatus,
    trialProjection: mergedTrialProjection,
  };
};

const describeTrialDaysRemaining = (daysRemaining) => {
  if (typeof daysRemaining !== "number" || !Number.isFinite(daysRemaining)) {
    return "";
  }
  if (daysRemaining <= 0) {
    return "Trial ends today.";
  }
  if (daysRemaining === 1) {
    return "1 day remaining in your trial.";
  }
  return `${daysRemaining} days remaining in your trial.`;
};

const buildStatusNotice = (status, trialProjection, fallbackMessage = "") => {
  const trialDays = describeTrialDaysRemaining(trialProjection?.trialDaysRemaining);

  if (status === "active") {
    return "This account already has active chat access.";
  }
  if (status === "trial_active") {
    return trialDays
      ? `${trialDays} Subscribe now to continue uninterrupted after your trial.`
      : "Your free trial is active. Subscribe now to continue uninterrupted.";
  }
  if (status === "trial_ending_soon") {
    return trialDays
      ? `${trialDays} Subscribe now so access continues after trial.`
      : "Your trial is ending soon. Subscribe now so access continues.";
  }
  if (status === "trial_pending_activation") {
    return "Trial started, access activation is in progress.";
  }
  if (status === "trial_activation_failed") {
    return "Trial started, but automatic activation failed. Please retry shortly or contact support.";
  }
  if (status === "trial_expired_access_paused") {
    return "Trial ended. Subscribe to restore access.";
  }
  if (status === "manual_review") {
    return "Your payment is under manual review. Access will activate after verification.";
  }
  if (status === "pending_activation") {
    return "Payment is verified and access activation is in progress.";
  }
  if (status === "activation_failed") {
    return "We could not activate your access automatically. Please contact support.";
  }
  if (fallbackMessage) {
    return fallbackMessage;
  }
  return "";
};

const mapBackendProjectionToUiStatus = (payload) => {
  const accessStatus = normalizeUiStatus(payload?.access_status);
  const status = normalizeUiStatus(payload?.status);
  const billingStatus = String(payload?.billing_status || "").trim().toLowerCase();

  if (payload?.chat_access === true || accessStatus === "active" || status === "active") {
    return "active";
  }

  if (
    accessStatus === "manual_review" ||
    status === "manual_review" ||
    billingStatus === "manual_review"
  ) {
    return "manual_review";
  }

  if (accessStatus === "activation_failed" || status === "activation_failed") {
    return "activation_failed";
  }

  if (accessStatus === "pending_activation" || status === "pending_activation") {
    return "pending_activation";
  }

  if (
    billingStatus === "pending_payment" ||
    billingStatus === "payment_verified" ||
    billingStatus === "subscription_active"
  ) {
    return "pending_activation";
  }

  if (accessStatus === "inactive" || status === "inactive") {
    return "inactive";
  }

  if (billingStatus === "cancelled" || billingStatus === "expired") {
    return "inactive";
  }

  // Conservative default: never claim active for unknown state.
  return "inactive";
};

const STATUS_POLL_INTERVAL_MS = 3000;
const STATUS_POLL_MAX_ATTEMPTS = 20;

const chatUrl =
  import.meta.env.VITE_CHAT_DOMAIN_URL || "https://chat.paralegal.lk";
const chatDomainLabel = chatUrl.replace(/^https?:\/\//, "").replace(/\/$/, "");
const chatDbServerUrl = CHAT_DB_SERVER_URL;
const paymentsServerUrl = PAYMENTS_SERVER_URL;

const ChatPricing = () => {
  const { state, signIn, getAccessToken, getBasicUserInfo, getDecodedIDToken } =
    useAuthContext();
  const [searchParams] = useSearchParams();
  const [modalState, setModalState] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isStartingTrial, setIsStartingTrial] = useState(false);
  const [isCheckingEntitlement, setIsCheckingEntitlement] = useState(false);
  const [isPollingStatus, setIsPollingStatus] = useState(false);
  const [runtimeStatus, setRuntimeStatus] = useState(null);
  const [trialProjection, setTrialProjection] = useState(null);
  const [activationNotice, setActivationNotice] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const pollingIntervalRef = useRef(null);
  const inFlightPollRef = useRef(false);

  const statusFromQuery = useMemo(() => {
    const value = searchParams.get("status");
    return normalizeUiStatus(value);
  }, [searchParams]);
  const orderIdFromQuery = useMemo(() => searchParams.get("order_id"), [searchParams]);
  const status = runtimeStatus || statusFromQuery;
  const isPaidActiveState = status === "active";
  const canOpenChat = status === "active" || status === "trial_active" || status === "trial_ending_soon";

  useEffect(() => {
    if (document.querySelector('script[src*="payhere.lk/lib/payhere.js"]')) {
      return;
    }

    const script = document.createElement("script");
    script.src = "https://www.payhere.lk/lib/payhere.js";
    script.defer = true;
    script.async = true;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const buildStatusUrl = (nextStatus, orderId = null) => {
    const url = new URL(window.location.href);
    url.searchParams.set("status", nextStatus);
    if (orderId) {
      url.searchParams.set("order_id", orderId);
    }
    return url.toString();
  };

  const updateStatusInUrl = useCallback((nextStatus, orderId = null) => {
    const url = new URL(window.location.href);
    url.searchParams.set("status", nextStatus);
    if (orderId) {
      url.searchParams.set("order_id", orderId);
    } else {
      url.searchParams.delete("order_id");
    }
    window.history.replaceState({}, "", url.toString());
  }, []);

  const stopStatusPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      window.clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    inFlightPollRef.current = false;
    setIsPollingStatus(false);
  }, []);

  const startStatusPolling = useCallback(
    (accessToken, orderId) => {
      if (!orderId || !accessToken) {
        return;
      }

      stopStatusPolling();
      setIsPollingStatus(true);
      setRuntimeStatus("pending_activation");
      setActivationNotice("Payment received. Activating your chat access...");
      updateStatusInUrl("pending_activation", orderId);

      let attempts = 0;
      pollingIntervalRef.current = window.setInterval(async () => {
        if (inFlightPollRef.current) {
          return;
        }

        inFlightPollRef.current = true;
        attempts += 1;

        try {
          const res = await fetch(
            `${chatDbServerUrl}/payment/status?order_id=${encodeURIComponent(orderId)}`,
            {
              method: "GET",
              headers: {
                Authorization: `Bearer ${accessToken}`,
              },
            }
          );

          const payload = await res.json().catch(() => ({}));

          if (res.status === 404) {
            return;
          }

          if (!res.ok) {
            throw new Error(payload?.detail || payload?.error || `HTTP ${res.status}`);
          }

          const resolved = resolveUiProjection(payload, trialProjection);
          if (resolved.trialProjection) {
            setTrialProjection(resolved.trialProjection);
          }
          const mappedStatus = resolved.status;

          if (mappedStatus === "active" || mappedStatus === "trial_active" || mappedStatus === "trial_ending_soon") {
            stopStatusPolling();
            setRuntimeStatus(mappedStatus);
            setActivationNotice(buildStatusNotice(mappedStatus, resolved.trialProjection));
            updateStatusInUrl(mappedStatus, orderId);
            return;
          }

          if (mappedStatus === "manual_review") {
            stopStatusPolling();
            setRuntimeStatus("manual_review");
            setActivationNotice(
              "Payment was received and is under review. Access will activate after manual verification."
            );
            updateStatusInUrl("manual_review", orderId);
            return;
          }

          if (mappedStatus === "activation_failed" || mappedStatus === "trial_activation_failed") {
            stopStatusPolling();
            setRuntimeStatus(mappedStatus);
            setActivationNotice(buildStatusNotice(mappedStatus, resolved.trialProjection));
            updateStatusInUrl(mappedStatus, orderId);
            return;
          }

          if (mappedStatus === "inactive" || mappedStatus === "trial_expired_access_paused") {
            stopStatusPolling();
            setRuntimeStatus(mappedStatus);
            setActivationNotice(buildStatusNotice(mappedStatus, resolved.trialProjection));
            updateStatusInUrl(mappedStatus, orderId);
            return;
          }

          if (mappedStatus === "pending_activation" || mappedStatus === "trial_pending_activation") {
            setRuntimeStatus(mappedStatus);
            setActivationNotice(buildStatusNotice(mappedStatus, resolved.trialProjection));
            updateStatusInUrl(mappedStatus, orderId);
            return;
          }

          if (payload?.status_code && String(payload.status_code) !== "2") {
            stopStatusPolling();
            setRuntimeStatus("activation_failed");
            setActivationNotice(buildStatusNotice("activation_failed", trialProjection));
            updateStatusInUrl("activation_failed", orderId);
            return;
          }
        } catch (err) {
          console.error("Subscription status poll failed:", err);
          if (attempts >= STATUS_POLL_MAX_ATTEMPTS) {
            stopStatusPolling();
            setRuntimeStatus("pending_activation");
            setActivationNotice(
              "Payment is still being confirmed. Please refresh in a moment."
            );
            updateStatusInUrl("pending_activation", orderId);
          }
        } finally {
          inFlightPollRef.current = false;
        }

        if (attempts >= STATUS_POLL_MAX_ATTEMPTS) {
          stopStatusPolling();
          setRuntimeStatus("pending_activation");
          setActivationNotice(
            "Activation is taking longer than expected. We will keep your payment and complete access shortly."
          );
          updateStatusInUrl("pending_activation", orderId);
        }
      }, STATUS_POLL_INTERVAL_MS);
    },
    [stopStatusPolling, trialProjection, updateStatusInUrl]
  );

  const triggerStatusPolling = useCallback(
    async (orderId) => {
      try {
        const token = await getAccessToken();
        if (!token) {
          throw new Error("Authentication token is missing.");
        }
        startStatusPolling(token, orderId);
      } catch (err) {
        console.error("Failed to start status polling:", err);
        setActivationNotice(
          "Payment completed. We could not auto-refresh access status. Please refresh this page."
        );
      }
    },
    [getAccessToken, startStatusPolling]
  );

  const startPaymentWithCallbacks = (paymentPayload, orderId) => {
    payhere.onCompleted = function onCompleted() {
      setTrialProjection((previous) =>
        previous
          ? {
              ...previous,
              trialStatus: "converted_to_paid",
              accessActionStatus: null,
            }
          : previous
      );
      setIsProcessing(false);
      setRuntimeStatus("pending_activation");
      updateStatusInUrl("pending_activation", orderId);
      triggerStatusPolling(orderId);
    };

    payhere.onDismissed = function onDismissed() {
      setIsProcessing(false);
    };

    payhere.onError = function onError(error) {
      console.error("PayHere payment error:", error);
      setIsProcessing(false);
      setRuntimeStatus("inactive");
      updateStatusInUrl("inactive", orderId);
      setErrorMessage("Payment failed. Please try again.");
      setModalState("payment_error");
    };

    payhere.startPayment(paymentPayload);
  };

  const requestPaymentHash = async (accessToken, orderId, amount, currency) => {
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    };

    const res = await fetch(`${paymentsServerUrl}/payment/start`, {
      method: "POST",
      headers,
      body: JSON.stringify({
        order_id: orderId,
        amount,
        currency,
      }),
    });

    const body = await res.json().catch(() => ({}));
    if (!res.ok) {
      const message = body.error || body.detail || `HTTP ${res.status}`;
      const err = new Error(message);
      err.status = res.status;
      throw err;
    }

    if (body?.order_id_used && body.order_id_used !== orderId) {
      throw new Error("Payment gateway returned mismatched order identity.");
    }

    return body;
  };

  const handleStartTrial = async () => {
    if (isStartingTrial || isProcessing || isPollingStatus) {
      return;
    }

    if (!state?.isAuthenticated) {
      setModalState("auth_required");
      return;
    }

    setIsStartingTrial(true);
    setErrorMessage("");
    setActivationNotice("");

    try {
      const accessToken = await getAccessToken();
      if (!accessToken) {
        throw new Error("Authentication token is missing.");
      }

      const trialResponse = await fetch(`${chatDbServerUrl}/chat-trials/start`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ plan_code: TRIAL_PLAN_CODE }),
      });

      const trialPayload = await trialResponse.json().catch(() => ({}));
      if (!trialResponse.ok) {
        throw new Error(trialPayload?.detail || "Failed to start free trial.");
      }

      const resolved = resolveUiProjection(trialPayload, trialProjection);
      if (resolved.trialProjection) {
        setTrialProjection(resolved.trialProjection);
      }
      setRuntimeStatus(resolved.status);
      updateStatusInUrl(resolved.status);
      setActivationNotice(
        String(trialPayload?.message || "").trim() ||
          buildStatusNotice(resolved.status, resolved.trialProjection)
      );
    } catch (error) {
      console.error("Trial start error:", error);
      setErrorMessage(error.message || "Failed to start free trial.");
      setModalState("trial_error");
    } finally {
      setIsStartingTrial(false);
    }
  };

  const handleStartSubscription = async () => {
    if (isProcessing || isStartingTrial || isPollingStatus || isPaidActiveState) {
      return;
    }

    if (!state?.isAuthenticated) {
      setModalState("auth_required");
      return;
    }

    if (typeof payhere === "undefined") {
      setErrorMessage("Payment script is still loading. Please try again in a moment.");
      setModalState("payment_error");
      return;
    }

    setIsProcessing(true);
    setErrorMessage("");
    setActivationNotice("");

    try {
      const accessToken = await getAccessToken();
      if (!accessToken) {
        throw new Error("Authentication token is missing.");
      }

      const [basicUserInfo, decodedToken] = await Promise.all([
        getBasicUserInfo(),
        getDecodedIDToken(),
      ]);

      const email = basicUserInfo?.email || decodedToken?.email;
      if (!email) {
        throw new Error("Unable to resolve user email from your account.");
      }

      const entitlementResponse = await fetch(
        `${chatDbServerUrl}/chat-entitlements/me`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      const entitlementData = await entitlementResponse.json().catch(() => ({}));
      const entitlementResolved = resolveUiProjection(entitlementData, trialProjection);
      const entitlementStatus = entitlementResolved.status;
      if (entitlementResolved.trialProjection) {
        setTrialProjection(entitlementResolved.trialProjection);
      }
      if (entitlementResponse.ok && entitlementStatus === "active") {
        setIsProcessing(false);
        setRuntimeStatus("active");
        updateStatusInUrl("active");
        setActivationNotice("This account already has active chat access.");
        return;
      }

      const checkoutResponse = await fetch(
        `${chatDbServerUrl}/chat-subscriptions/checkout/start`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            return_url: buildStatusUrl("pending"),
            cancel_url: buildStatusUrl("failed"),
          }),
        }
      );

      const checkoutData = await checkoutResponse.json().catch(() => ({}));
      if (!checkoutResponse.ok) {
        throw new Error(
          checkoutData?.detail || "Failed to create subscription checkout session."
        );
      }

      const formattedAmount = Number(checkoutData.amount).toFixed(2);
      const orderId = checkoutData.order_id;
      const currency = checkoutData.currency || "LKR";

      const hashData = await requestPaymentHash(
        accessToken,
        orderId,
        formattedAmount,
        currency
      );

      if (!hashData?.hash || !hashData?.merchant_id) {
        throw new Error("Payment hash generation failed.");
      }

      if (!NOTIFY_URL) {
        throw new Error("Payment notify URL is not configured.");
      }

      const paymentPayload = {
        sandbox: import.meta.env.VITE_SANDBOX !== "false",
        merchant_id: hashData.merchant_id,
        return_url: buildStatusUrl("pending"),
        cancel_url: buildStatusUrl("failed"),
        notify_url: NOTIFY_URL,
        order_id: orderId,
        items: `${checkoutData.plan_name || "Chat Pro"} Subscription`,
        amount: formattedAmount,
        currency,
        first_name: basicUserInfo?.givenName || decodedToken?.given_name || "",
        last_name: basicUserInfo?.familyName || decodedToken?.family_name || "",
        email,
        phone: "",
        address: "",
        city: "",
        country: "Sri Lanka",
        hash: hashData.hash,
        custom_1: "individual",
        custom_2: checkoutData.plan_code || "CHAT_PRO_MONTHLY",
      };

      startPaymentWithCallbacks(paymentPayload, paymentPayload.order_id);
    } catch (error) {
      console.error("Subscription checkout error:", error);
      setErrorMessage(error.message || "Failed to initialize payment.");
      setModalState("payment_error");
      setIsProcessing(false);
    }
  };

  const handleLogin = () => {
    sessionStorage.setItem(
      "redirectAfterLogin",
      `${window.location.pathname}${window.location.search}`
    );
    signIn();
  };

  useEffect(() => {
    let cancelled = false;

    const checkEntitlementOnLoad = async () => {
      if (!state?.isAuthenticated) {
        setIsCheckingEntitlement(false);
        return;
      }

      setIsCheckingEntitlement(true);
      try {
        const accessToken = await getAccessToken();
        if (!accessToken || cancelled) {
          return;
        }

        const res = await fetch(`${chatDbServerUrl}/chat-entitlements/me`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });
        const payload = await res.json().catch(() => ({}));

        if (cancelled || !res.ok) {
          return;
        }

        const resolved = resolveUiProjection(payload, trialProjection);
        if (resolved.trialProjection) {
          setTrialProjection(resolved.trialProjection);
        }

        setRuntimeStatus(resolved.status);
        if (resolved.status === "active") {
          stopStatusPolling();
          updateStatusInUrl("active");
        }
        setActivationNotice(buildStatusNotice(resolved.status, resolved.trialProjection));
      } catch (error) {
        console.error("Initial entitlement check failed:", error);
      } finally {
        if (!cancelled) {
          setIsCheckingEntitlement(false);
        }
      }
    };

    checkEntitlementOnLoad();

    return () => {
      cancelled = true;
    };
  }, [state?.isAuthenticated, getAccessToken, stopStatusPolling, updateStatusInUrl]);

  useEffect(() => {
    return () => {
      stopStatusPolling();
    };
  }, [stopStatusPolling]);

  useEffect(() => {
    if (!state?.isAuthenticated || !orderIdFromQuery) {
      return;
    }

    if (statusFromQuery !== "pending_activation") {
      return;
    }

    triggerStatusPolling(orderIdFromQuery);
  }, [state?.isAuthenticated, orderIdFromQuery, statusFromQuery, triggerStatusPolling]);

  const trialStatus = normalizeTrialLifecycleStatus(trialProjection?.trialStatus);
  const trialEligibleFromBackend = trialProjection?.backendEligible;
  const trialAlreadyUsed =
    trialProjection?.accessActionStatus === "trial_already_used" ||
    trialStatus === "converted_to_paid" ||
    trialStatus === "expired" ||
    trialStatus === "access_removal_started" ||
    status === "trial_expired_access_paused";
  const isTrialActiveState = status === "trial_active" || status === "trial_ending_soon";
  const isTrialPendingActivationState =
    status === "trial_pending_activation" || status === "trial_activation_failed";
  const isTrialExpiredState = status === "trial_expired_access_paused";
  const hasTrialUiState = isTrialActiveState || isTrialPendingActivationState || isTrialExpiredState;
  const shouldShowTrialStartCta =
    trialEligibleFromBackend === true &&
    !isPaidActiveState &&
    !hasTrialUiState &&
    !trialAlreadyUsed &&
    status !== "pending_activation" &&
    status !== "manual_review";
  const shouldShowTrialUsedNote =
    state?.isAuthenticated &&
    trialEligibleFromBackend === false &&
    !isPaidActiveState &&
    !hasTrialUiState &&
    status !== "pending_activation" &&
    status !== "manual_review";
  const formattedTrialEndsAt = formatTrialEndDate(trialProjection?.trialEndsAt);
  const trialDaysRemaining =
    typeof trialProjection?.trialDaysRemaining === "number" &&
    Number.isFinite(trialProjection.trialDaysRemaining)
      ? Math.max(0, Math.ceil(trialProjection.trialDaysRemaining))
      : null;
  const trialPrimaryMessage =
    status === "trial_ending_soon"
      ? "Subscribe now to keep access after the trial."
      : status === "trial_active"
      ? "Subscribe now to continue without interruption after your trial ends."
      : status === "trial_expired_access_paused"
      ? "Subscribe to continue using the service."
      : "";

  const showStartTrialButton =
    state?.isAuthenticated &&
    !isCheckingEntitlement &&
    !isProcessing &&
    !isStartingTrial &&
    !isPollingStatus &&
    shouldShowTrialStartCta;

  const startButtonDisabled =
    isCheckingEntitlement ||
    isProcessing ||
    isStartingTrial ||
    isPollingStatus ||
    isPaidActiveState ||
    status === "pending_activation" ||
    status === "manual_review" ||
    status === "trial_pending_activation";
  const startButtonLabel = isCheckingEntitlement
    ? "Checking Access..."
    : isPaidActiveState
    ? "Subscription Active"
    : isStartingTrial
    ? "Starting Trial..."
    : isPollingStatus || status === "pending_activation"
    ? "Activating Access..."
    : status === "trial_pending_activation"
    ? "Activating Access..."
    : status === "trial_active" || status === "trial_ending_soon"
    ? "Subscribe Now"
    : status === "trial_expired_access_paused"
    ? "Start Subscription"
    : status === "manual_review"
    ? "Under Review"
    : isProcessing
    ? "Processing..."
    : "Start Subscription";

  const modalConfig =
    modalState === "auth_required"
      ? {
          title: "Login required",
          description: "Please log in to continue to checkout.",
          actions: [
            { label: "Close", onClick: () => setModalState(null) },
            { label: "Log in", onClick: handleLogin, variant: "primary" },
          ],
        }
      : modalState === "payment_error"
      ? {
          title: "Payment initialization failed",
          description: errorMessage || "Unable to start payment. Please try again.",
          actions: [
            { label: "Close", onClick: () => setModalState(null), variant: "primary" },
          ],
        }
      : modalState === "trial_error"
      ? {
          title: "Trial start failed",
          description: errorMessage || "Unable to start trial. Please try again.",
          actions: [
            { label: "Close", onClick: () => setModalState(null), variant: "primary" },
          ],
        }
      : null;

  return (
    <MainLayout>
      <Helmet>
        <title>Chat | paralegal.lk</title>
        <meta
          name="description"
          content="Subscribe to Paralegal’s dedicated chat workspace for AI-assisted legal research, drafting, and analysis."
        />
        <link rel="canonical" href="https://www.paralegal.lk/chat" />
      </Helmet>

      <div className="w-full max-w-6xl px-6 py-10 text-pl-text-primary md:px-10 md:py-14">
        <ChatStatusBanner
          status={status}
          trialDaysRemaining={trialProjection?.trialDaysRemaining}
          trialEndsAt={trialProjection?.trialEndsAt}
        />

        <section className="rounded-[24px] border border-pl-border bg-pl-bg-elevated p-8 shadow-pl-md md:p-12">
          <div className="grid gap-10 lg:grid-cols-[1.2fr_0.8fr] lg:items-start">
            <div>
              <p className="text-12 font-semibold uppercase tracking-[0.22em] text-pl-accent">
                Subscription Workspace for Legal Professionals
              </p>
              <h1 className="mt-4 max-w-3xl font-display text-36 font-semibold leading-tight md:text-[52px]">
                AI legal research and drafting for Sri Lankan legal practice.
              </h1>
              <p className="mt-5 max-w-2xl text-16 leading-8 text-pl-text-secondary md:text-18">
                <a
                  href={chatUrl}
                  className="text-pl-accent underline underline-offset-4 hover:text-pl-accent-hover"
                >
                  {chatDomainLabel}
                </a>{" "}
                is Paralegal’s dedicated subscription workspace for deeper
                legal research, drafting, and analysis. Work faster from first
                question to final written output in one focused environment.
              </p>

              <div className="mt-8 flex flex-col gap-4 sm:flex-row">
                <button
                  type="button"
                  onClick={handleStartSubscription}
                  disabled={startButtonDisabled}
                  className={`rounded-pl px-6 py-3 text-15 font-semibold text-white transition-colors ${
                    startButtonDisabled
                      ? "cursor-not-allowed bg-pl-text-tertiary"
                      : "bg-pl-accent hover:bg-pl-accent-hover"
                  }`}
                  aria-label="Start monthly chat subscription"
                >
                  {startButtonLabel}
                </button>
                {showStartTrialButton && (
                  <button
                    type="button"
                    onClick={handleStartTrial}
                    className="inline-flex items-center justify-center rounded-pl border border-pl-accent bg-transparent px-6 py-3 text-15 font-semibold text-pl-accent transition-colors duration-150 hover:bg-pl-accent hover:text-white"
                    aria-label="Start free trial"
                  >
                    Start 14-Day Free Trial
                  </button>
                )}
                {canOpenChat && (
                  <a
                    href={chatUrl}
                    className="inline-flex items-center justify-center rounded-pl border border-pl-accent bg-transparent px-6 py-3 text-15 font-semibold text-pl-accent transition-colors duration-150 hover:bg-pl-accent hover:text-white"
                  >
                    Open Chat
                  </a>
                )}
                <Link
                  to="/about"
                  className="rounded-pl border border-pl-border bg-pl-bg px-6 py-3 text-15 font-semibold text-pl-text-primary transition-colors hover:bg-pl-bg-subtle"
                >
                  Learn about Paralegal
                </Link>
              </div>

              {activationNotice && (
                <p className="mt-3 text-14 leading-7 text-pl-text-secondary">
                  {activationNotice}
                </p>
              )}

              {showStartTrialButton && (
                <p className="mt-3 text-13 text-pl-text-secondary">
                  No card required. One free trial per account.
                </p>
              )}

              {isTrialActiveState && (
                <div className="mt-4 rounded-pl border border-pl-border bg-pl-bg-subtle px-4 py-3">
                  <p className="text-14 font-semibold text-pl-text-primary">
                    {status === "trial_ending_soon"
                      ? "Your free trial ends soon"
                      : "Free trial active"}
                  </p>
                  {formattedTrialEndsAt && (
                    <p className="mt-1 text-13 text-pl-text-secondary">
                      Ends on: {formattedTrialEndsAt}
                    </p>
                  )}
                  {trialDaysRemaining !== null && (
                    <p className="mt-1 text-13 text-pl-text-secondary">
                      {trialDaysRemaining} day{trialDaysRemaining === 1 ? "" : "s"} remaining
                    </p>
                  )}
                  <p className="mt-1 text-13 text-pl-text-secondary">{trialPrimaryMessage}</p>
                </div>
              )}

              {isTrialExpiredState && (
                <div className="mt-4 rounded-pl border border-pl-border bg-pl-bg-subtle px-4 py-3">
                  <p className="text-14 font-semibold text-pl-text-primary">
                    Your free trial has ended.
                  </p>
                  <p className="mt-1 text-13 text-pl-text-secondary">
                    Subscribe to continue using the service.
                  </p>
                </div>
              )}

              {shouldShowTrialUsedNote && (
                <p className="mt-3 text-13 text-pl-text-secondary">
                  Free trial already used for this account.
                </p>
              )}

              <p className="mt-4 text-14 font-semibold text-pl-text-primary">
                Access is available to registered subscribers only.
              </p>
              <p className="mt-2 max-w-2xl text-14 leading-7 text-pl-text-secondary">
                Sign in and subscribe to access{" "}
                <a
                  href={chatUrl}
                  className="text-pl-accent underline underline-offset-4 hover:text-pl-accent-hover"
                >
                  {chatDomainLabel}
                </a>
                . Already subscribed? Sign in with your Paralegal account.
              </p>
              <p className="mt-4 text-13 text-pl-text-tertiary">
                Checkout is handled securely through PayHere and subscription
                access is enabled after payment confirmation.
              </p>
            </div>

            <aside className="rounded-[20px] border border-pl-border bg-pl-bg-subtle p-6 shadow-pl-sm">
              <p className="text-12 font-semibold uppercase tracking-[0.18em] text-pl-accent">
                Monthly Plan
              </p>
              <h2 className="mt-3 font-display text-24 font-semibold text-pl-text-primary">
                Paralegal Chat Pro
              </h2>
              <div className="mt-4 flex items-end gap-2">
                <span className="font-display text-36 font-semibold">Rs. 20,000</span>
                <span className="pb-1 text-15 text-pl-text-secondary">/ month</span>
              </div>
              <p className="mt-4 text-14 leading-7 text-pl-text-secondary">
                One straightforward subscription tier designed for legal
                professionals and legal teams.
              </p>

              <ul className="mt-6 space-y-3">
                {INCLUDED_ITEMS.map((item) => (
                  <li key={item} className="flex gap-3 text-14 leading-6 text-pl-text-primary">
                    <span
                      className="mt-[6px] h-2.5 w-2.5 rounded-full bg-pl-accent"
                      aria-hidden="true"
                    />
                    <span>
                      {item === "Monthly access to chat.paralegal.lk" ? (
                        <>
                          Monthly access to{" "}
                          <a
                            href={chatUrl}
                            className="text-pl-accent underline underline-offset-4 hover:text-pl-accent-hover"
                          >
                            {chatDomainLabel}
                          </a>
                        </>
                      ) : (
                        item
                      )}
                    </span>
                  </li>
                ))}
              </ul>

              <button
                type="button"
                onClick={handleStartSubscription}
                disabled={startButtonDisabled}
                className={`mt-8 w-full rounded-pl px-6 py-3 text-15 font-semibold text-white transition-colors ${
                  startButtonDisabled
                    ? "cursor-not-allowed bg-pl-text-tertiary"
                    : "bg-pl-accent hover:bg-pl-accent-hover"
                }`}
              >
                {startButtonLabel}
              </button>
              {showStartTrialButton && (
                <button
                  type="button"
                  onClick={handleStartTrial}
                  className="mt-3 w-full rounded-pl border border-pl-accent bg-transparent px-6 py-3 text-15 font-semibold text-pl-accent transition-colors hover:bg-pl-accent hover:text-white"
                >
                  Start 14-Day Free Trial
                </button>
              )}

              <p className="mt-4 text-12 leading-6 text-pl-text-tertiary">
                By continuing, you agree to our{" "}
                <Link to="/terms" className="text-pl-accent hover:underline">
                  Terms
                </Link>
                ,{" "}
                <Link to="/privacy" className="text-pl-accent hover:underline">
                  Privacy Policy
                </Link>
                , and{" "}
                <Link to="/refund" className="text-pl-accent hover:underline">
                  Refund Policy
                </Link>
                .
              </p>
            </aside>
          </div>
        </section>

        <section className="mt-8 rounded-[24px] border border-pl-border bg-pl-bg-elevated p-8 shadow-pl-md md:p-12">
          <div className="grid gap-8 lg:grid-cols-[1fr_0.9fr] lg:items-start">
            <div>
              <h2 className="font-display text-28 font-semibold text-pl-text-primary md:text-36">
                Need access for your firm or team?
              </h2>
              <p className="mt-4 max-w-3xl text-16 leading-8 text-pl-text-secondary">
                For chambers, law firms, and legal teams that need multi-user
                access, shared onboarding, and tailored support, choose a group
                subscription.
              </p>
            </div>

            <aside className="rounded-[20px] border border-pl-border bg-pl-bg-subtle p-6 shadow-pl-sm">
              <h3 className="font-display text-24 font-semibold text-pl-text-primary">
                Group Subscription
              </h3>
              <p className="mt-4 text-14 leading-7 text-pl-text-secondary">
                Contact us for team pricing and onboarding tailored to your
                organization.
              </p>

              <ul className="mt-6 space-y-3">
                {GROUP_PLAN_HIGHLIGHTS.map((item) => (
                  <li key={item} className="flex gap-3 text-14 leading-6 text-pl-text-primary">
                    <span
                      className="mt-[6px] h-2.5 w-2.5 rounded-full bg-pl-accent"
                      aria-hidden="true"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              <a
                href="mailto:admin@paralegal.lk?subject=Group%20Subscription%20Inquiry"
                className="mt-8 inline-flex w-full items-center justify-center rounded-pl border border-pl-accent bg-pl-bg-elevated px-6 py-3 text-15 font-semibold text-pl-accent transition-colors hover:bg-pl-accent-subtle"
              >
                Contact Us
              </a>

              <p className="mt-4 text-12 leading-6 text-pl-text-tertiary">
                Prefer to discuss requirements first? Contact us for a tailored
                plan.
              </p>
            </aside>
          </div>
        </section>

        <section className="mt-8 grid gap-5 md:grid-cols-3">
          {PRODUCT_FEATURES.map((feature) => (
            <article
              key={feature.title}
              className="rounded-[20px] border border-pl-border bg-pl-bg-elevated p-6 shadow-pl-sm"
            >
              <h2 className="font-display text-24 font-semibold text-pl-text-primary">
                {feature.title}
              </h2>
              <p className="mt-3 text-15 leading-7 text-pl-text-secondary">
                {feature.description}
              </p>
            </article>
          ))}
        </section>
      </div>

      <DialogModal
        isOpen={Boolean(modalConfig)}
        onClose={() => setModalState(null)}
        title={modalConfig?.title}
        description={modalConfig?.description}
        actions={modalConfig?.actions}
      />
    </MainLayout>
  );
};

export default ChatPricing;
